<script lang="ts">
	import type { PageProps } from "./$types";
	import { ActionGrid } from "#lib/web-ui/fergmar-com/index.js";
	import { serializeScriptValue } from "#lib/web-ui/home-buyers-template/head-scripts.js";
	import { FeaturedInvestments, PodcastSection } from "#lib/web-ui/blocks/index.js";
	import { Section } from "#lib/web-ui/primitives/index.js";
	import {
		cloudinaryUrl,
		cloudinaryVideoUrl,
		cloudinaryVideoPoster
	} from "#lib/shared/cloudinary.js";
	import ArrowUpRightIcon from "@lucide/svelte/icons/arrow-up-right";
	import {
		assetsUnderManagement,
		doorsUnderManagement,
		transactionsClosed,
		transactionVolume
	} from "#lib/constants/shared-site-config.js";

	let { data }: PageProps = $props();

	const stats = [
		{ value: `${assetsUnderManagement}+`, label: "Assets Under Management" },
		{ value: `${doorsUnderManagement}+`, label: "Managed Doors" },
		{ value: `${transactionsClosed}+`, label: "Transactions" },
		{ value: `${transactionVolume}+`, label: "Transaction Volume" }
	];

	const divisions = [
		{
			name: "Fergmar Capital",
			logo: cloudinaryUrl("brand/fergmar-capital/fergmar-capital-long-dark", { width: 300 }),
			description:
				"Passive real estate investing. We offer growth and income offerings, giving investors access to multifamily and commercial properties in growing markets.",
			href: "https://fergmarcapital.com",
			external: true,
			color: "#33b47b"
		},
		{
			name: "Fergmar Properties",
			logo: cloudinaryUrl("brand/fergmar-properties/fergmar-properties-long-dark", {
				width: 300
			}),
			description:
				"Find your next home, office, or investment. Apartments and homes for rent, commercial spaces for lease, and properties for sale including new construction and land.",
			href: "/properties",
			external: false,
			color: "#1F6FEB"
		},
		{
			name: "Fergmar Home Buyers",
			logo: cloudinaryUrl("brand/fergmar-home-buyers/fergmar-home-buyers-long-dark", {
				width: 300
			}),
			description:
				"We purchase properties and land through multiple options including fast cash offers or professional listing and marketing services designed to maximize your overall return.",
			href: "https://fergmarhomebuyers.com",
			external: true,
			color: "#f79222"
		}
	];

	const localBusinessSchema = $derived({
		"@context": "https://schema.org",
		"@type": "RealEstateAgent",
		name: "Fergmar",
		slogan: "Enhancing Communities through Real Estate",
		url: "https://fergmar.com",
		logo: cloudinaryUrl("brand/fergmar/fergmar-logo-dark", { width: 300, trim: true }),
		image: cloudinaryUrl("www/fergmar-com/improving-communities-1", { width: 1200 }),
		telephone: data.contactInfo.phone,
		email: data.contactInfo.email,
		address: {
			"@type": "PostalAddress",
			streetAddress: data.contactInfo.address.street + " " + data.contactInfo.address.suite,
			addressLocality: data.contactInfo.address.city,
			addressRegion: data.contactInfo.address.state,
			postalCode: data.contactInfo.address.zip,
			addressCountry: "US"
		},
		sameAs: [data.contactInfo.socialLinks.facebook, data.contactInfo.socialLinks.linkedin],
		openingHoursSpecification: [
			{
				"@type": "OpeningHoursSpecification",
				dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
				opens: "08:00",
				closes: "17:00"
			}
		],
		priceRange: "$$",
		areaServed: {
			"@type": "City",
			name: "Victoria",
			containedInPlace: {
				"@type": "State",
				name: "Texas"
			}
		},
		description:
			"Buy, sell, build, rent, or invest in Victoria, TX with Fergmar. We enhance communities through real estate."
	});
</script>

<svelte:head>
	<title>Real Estate Solutions in Victoria, TX | Fergmar</title>
	<svelte:element this={"script"} type="application/ld+json"
		>{serializeScriptValue(localBusinessSchema)}</svelte:element
	>
	<meta
		name="description"
		content="Buy, sell, build, rent, or invest in Victoria, TX with Fergmar. We enhance communities through real estate."
	/>
	<meta
		name="keywords"
		content="real estate, investment, asset management, property management, development, construction, capital markets, advisory, communities, value creation, Fergmar"
	/>
	<meta name="viewport" content="width=device-width, initial-scale=1.0" />
	<meta property="og:title" content="Real Estate Solutions in Victoria, TX | Fergmar" />
	<meta property="og:site_name" content="Fergmar" />
	<meta
		property="og:description"
		content="Buy, sell, build, rent, or invest in Victoria, TX with Fergmar. We enhance communities through real estate."
	/>
</svelte:head>

<!-- 1. Hero — video background -->
<section class="bg-foreground relative isolate overflow-hidden">
	<video
		autoplay
		muted
		loop
		playsinline
		preload="metadata"
		poster={cloudinaryVideoPoster("fergmar-com/hero-aerial", { width: 768 })}
		class="absolute inset-0 -z-20 size-full object-cover opacity-30"
	>
		<source
			src={cloudinaryVideoUrl("fergmar-com/hero-aerial", { width: 1280 })}
			media="(min-width: 768px)"
			type="video/mp4"
		/>
		<source
			src={cloudinaryVideoUrl("fergmar-com/hero-aerial", { width: 480 })}
			type="video/mp4"
		/>
	</video>
	<div class="bg-foreground/65 absolute inset-0 -z-10"></div>

	<div class="mx-auto max-w-5xl px-6 lg:px-8">
		<div class="mx-auto max-w-3xl py-20 sm:py-28 lg:py-36">
			<div class="text-center">
				<p
					class="text-primary-foreground/60 text-xs font-semibold tracking-[0.2em] uppercase"
				>
					Our Mission
				</p>
				<h1
					class="text-primary-foreground mt-3 text-4xl font-semibold tracking-tight text-balance sm:text-6xl"
				>
					Enhancing communities through real estate
				</h1>
				<p
					class="text-primary-foreground/80 mx-auto mt-8 max-w-2xl text-lg font-medium text-pretty sm:text-xl/8"
				>
					We create spaces where people can live, work, and thrive. Through strategic
					acquisitions, hands-on asset management, and a deep commitment to the
					neighborhoods we operate in, we deliver exceptional results for our partners.
				</p>
			</div>
		</div>
	</div>

	<!-- Stats bar -->
	<div class="border-t border-white/10 bg-white/[0.04] backdrop-blur-sm">
		<div
			class="mx-auto grid max-w-5xl grid-cols-2 gap-y-4 px-6 py-5 sm:grid-cols-4 sm:gap-y-0 sm:py-6 lg:px-8"
		>
			{#each stats as stat (stat.label)}
				<div class="text-center">
					<p class="text-primary-foreground text-2xl font-bold sm:text-3xl">
						{stat.value}
					</p>
					<p
						class="text-primary-foreground/60 mt-1 text-xs font-medium tracking-wider uppercase"
					>
						{stat.label}
					</p>
				</div>
			{/each}
		</div>
	</div>
</section>

<!-- Gradient divider -->
<div
	class="h-2 w-full"
	style="background: linear-gradient(to right, #33b47b 25%, #1F6FEB 40%, #1F6FEB 60%, #f79222 75%)"
></div>

<!-- 2. Brands / Divisions -->
<Section theme="dark" class="!pt-16 !pb-20 md:!pt-20 md:!pb-28">
	<div class="mx-auto mb-16 text-center">
		<p class="text-xs font-semibold tracking-[0.2em] uppercase" style="color: #1F6FEB">
			Vertically Integrated
		</p>
		<h2 class="text-foreground mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
			One vision, full control from capital to keys
		</h2>
		<p
			class="text-muted-foreground mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-balance"
		>
			We leverage deep industry knowledge, innovative strategies, and a hands-on approach to
			maximize property value and create sustainable growth. From strategic acquisitions and
			asset management to property operations and development, we offer comprehensive
			solutions that cater to the unique needs of each project, committed to maximizing value
			for our partners and creating lasting positive impact on the neighborhoods we serve.
		</p>
	</div>

	<div class="grid grid-cols-1 sm:grid-cols-3 sm:divide-x sm:divide-white/[0.08]">
		{#each divisions as division (division.name)}
			<a
				href={division.href}
				target={division.external ? "_blank" : undefined}
				rel={division.external ? "noopener noreferrer" : undefined}
				class="group flex cursor-pointer flex-col items-center text-center transition-colors duration-300 hover:bg-white/[0.03]"
			>
				<!-- Solid color bar + gradient fade down -->
				<div class="w-full">
					<div class="h-2" style="background-color: {division.color}"></div>
					<div
						class="h-8"
						style="background: linear-gradient(to bottom, {division.color}25, transparent)"
					></div>
				</div>

				<!-- Content -->
				<div class="flex flex-1 flex-col px-6 pb-10 sm:px-8">
					<div class="flex h-16 items-center justify-center">
						<img
							src={division.logo}
							alt={division.name}
							class="h-12 w-auto object-contain sm:h-[3.75rem]"
							width="300"
							height="48"
							loading="lazy"
						/>
					</div>
					<p class="text-muted-foreground mt-3 text-center text-base leading-relaxed">
						{division.description}
					</p>
					<span
						class="mt-auto flex items-center justify-center gap-1.5 pt-6 text-sm font-bold tracking-wider uppercase transition-colors duration-300"
						style="color: {division.color}"
					>
						Visit Brand
						<ArrowUpRightIcon
							class="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
						/>
					</span>
				</div>
			</a>
		{/each}
	</div>
</Section>

<!-- 3. Services (ActionGrid) -->
<ActionGrid theme="light" />

<!-- 4. Featured Investments -->
<FeaturedInvestments
	label="Portfolio Highlights"
	title="Featured Investments"
	subtitle="Explore our diverse portfolio of investment properties that demonstrate our strategic approach to value creation across the crossroads region."
	investments={data.featuredInvestments}
	theme="dark"
/>

<!-- 5. Podcast -->
<PodcastSection
	label="Investment Insights"
	title="Investment Insights & Expertise"
	subtitle="Hear Brian Ferguson discuss proven investment management strategies, capital allocation principles, and wealth-building approaches that drive results for our investors."
	episodes={data.podcastEpisodes}
	theme="muted"
/>
